type CushionWorkTime = {
  id?: number | string | null
  startedAt?: string | null
  completedAt?: string | null
  timeWorkedSeconds?: number | null
}

type CushionSortKey = {
  timeWorkedSeconds: number
  wallClockSeconds: number
  id: number
}

function cushionSortKey(work: CushionWorkTime): CushionSortKey {
  const startedAt = work.startedAt ? new Date(work.startedAt).getTime() : null
  const completedAt = work.completedAt
    ? new Date(work.completedAt).getTime()
    : null
  const wallClockSeconds =
    startedAt != null && completedAt != null
      ? (completedAt - startedAt) / 1000
      : 0

  return {
    timeWorkedSeconds: work.timeWorkedSeconds ?? 0,
    wallClockSeconds,
    id: Number(work.id ?? 0),
  }
}

function compareCushionWork(a: CushionWorkTime, b: CushionWorkTime) {
  const aKey = cushionSortKey(a)
  const bKey = cushionSortKey(b)
  if (aKey.timeWorkedSeconds !== bKey.timeWorkedSeconds) {
    return bKey.timeWorkedSeconds - aKey.timeWorkedSeconds
  }
  if (aKey.wallClockSeconds !== bKey.wallClockSeconds) {
    return bKey.wallClockSeconds - aKey.wallClockSeconds
  }
  return aKey.id - bKey.id
}

function minutesOnJobForCushionCharge(
  shiftStartsAt: string,
  work: CushionWorkTime
) {
  if (!work.completedAt) {
    return null
  }
  const completedAt = new Date(work.completedAt).getTime()
  const shiftStart = new Date(shiftStartsAt).getTime()
  const startedAt = work.startedAt ? new Date(work.startedAt).getTime() : null
  const windowStart =
    startedAt == null ? shiftStart : Math.min(shiftStart, startedAt)
  return (completedAt - windowStart) / 60000
}

export function countChargedExtraWorkers({
  workers,
  workersNeeded,
  shiftStartsAt,
  chargeAfterMinutes,
}: {
  workers: CushionWorkTime[]
  workersNeeded: number
  shiftStartsAt: string
  chargeAfterMinutes: number | null
}) {
  if (chargeAfterMinutes == null || workersNeeded >= workers.length) {
    return 0
  }

  const extraWorkers = [...workers]
    .sort(compareCushionWork)
    .slice(workersNeeded)

  return extraWorkers.filter((work) => {
    const minutesOnJob = minutesOnJobForCushionCharge(shiftStartsAt, work)
    return minutesOnJob != null && minutesOnJob > chargeAfterMinutes
  }).length
}

function extraWorkersPhrase(count: number) {
  return count === 1 ? '1 extra worker' : `${count} extra workers`
}

function shiftSummary(extraWorkers: number, chargedExtraWorkers: number) {
  const scheduled = extraWorkersPhrase(extraWorkers)
  if (chargedExtraWorkers <= 0) {
    return `${scheduled} scheduled, 0 billed.`
  }
  return `${scheduled} scheduled, ${chargedExtraWorkers} charged.`
}

export type ExtraWorkersNotice = {
  label: string
  chargesExtraWorkers: boolean
  shiftSummary: string | null
}

export function describeExtraWorkers({
  extraWorkers,
  chargedExtraWorkers,
  chargeAfterMinutes,
  shiftHasEnded,
}: {
  extraWorkers: number
  chargedExtraWorkers: number
  chargeAfterMinutes: number | null
  shiftHasEnded: boolean
}): ExtraWorkersNotice | null {
  if (extraWorkers <= 0) {
    return null
  }
  if (chargeAfterMinutes == null) {
    return {
      label: `${extraWorkers} Extra Workers - on us!`,
      chargesExtraWorkers: false,
      shiftSummary: null,
    }
  }

  const summary = shiftSummary(extraWorkers, chargedExtraWorkers)
  if (!shiftHasEnded) {
    return {
      label: `${extraWorkersPhrase(
        extraWorkers
      )} scheduled, charged if they work ${chargeAfterMinutes}+ min`,
      chargesExtraWorkers: true,
      shiftSummary: summary,
    }
  }
  if (chargedExtraWorkers <= 0) {
    return {
      label: 'No charge for extra workers',
      chargesExtraWorkers: false,
      shiftSummary: summary,
    }
  }
  return {
    label: `Charged for ${extraWorkersPhrase(chargedExtraWorkers)}`,
    chargesExtraWorkers: true,
    shiftSummary: summary,
  }
}

export function formatChargeAfterWindow(chargeAfterMinutes: number) {
  return chargeAfterMinutes === 1 ? '1 minute' : `${chargeAfterMinutes} minutes`
}

export function formatMinimumPayPolicyLength(minPayLengthMinutes: number) {
  const hours = minPayLengthMinutes / 60
  const unit = hours === 1 ? 'hour' : 'hours'
  return `${hours} ${unit}`
}
